import { useEffect, useRef, useState } from 'react';
import type { FlowComposerMessage } from '@shared/types/flow-composer';

// The backend persists the user's message before any model call and closes the
// turn with an assistant message (answer, explicit failure or stop). A
// conversation whose last message is the user's is therefore still being worked
// on server-side, e.g. after a reload or a closed page: poll until the turn closes.
const POLL_INTERVAL_MS = 10_000;
// Fallback for a server that does not report `turnRunning`: Cloud Run ends any
// request after one hour, so an older turn can no longer close on its own.
const MAX_TURN_AGE_MS = 60 * 60 * 1000;

export type PendingTurnState = 'none' | 'pending' | 'interrupted';

/**
 * `turnRunning` is the server's verdict (its worker beats while it runs; see
 * shared/forge-turn.ts): false means the worker is gone, so the turn is shown as
 * interrupted at once instead of after an hour.
 */
export const getPendingTurnState = (
  messages: FlowComposerMessage[],
  now: number = Date.now(),
  turnRunning?: boolean,
): PendingTurnState => {
  const last = messages[messages.length - 1];
  if (!last || last.role !== 'user') return 'none';
  if (turnRunning !== undefined) return turnRunning ? 'pending' : 'interrupted';
  const age = now - Date.parse(last.createdAt);
  return Number.isFinite(age) && age > MAX_TURN_AGE_MS ? 'interrupted' : 'pending';
};

const readMessages = (payload: unknown): FlowComposerMessage[] | null => {
  if (typeof payload !== 'object' || payload === null) return null;
  const messages = (payload as { messages?: unknown }).messages;
  return Array.isArray(messages) ? (messages as FlowComposerMessage[]) : null;
};

const readTurnRunning = (payload: unknown): boolean | undefined => {
  if (typeof payload !== 'object' || payload === null) return undefined;
  const running = (payload as { turnRunning?: unknown }).turnRunning;
  return typeof running === 'boolean' ? running : undefined;
};

export interface ServerTurnVerdict {
  messageId: string | null;
  running?: boolean;
}

/** The verdict a payload carries about its own last message. */
export const readServerTurnVerdict = (payload: unknown): ServerTurnVerdict => {
  const messages = readMessages(payload);
  return { messageId: messages?.[messages.length - 1]?.id ?? null, running: readTurnRunning(payload) };
};

export const usePendingComposerTurn = (params: {
  conversationId: string | null;
  messages: FlowComposerMessage[];
  /** Server verdict carried by the last payload applied to the page, with the
   * message it was given for: a verdict on an earlier turn does not count. */
  serverTurn?: ServerTurnVerdict;
  /** True while this page owns a request (send, resume): no polling then. */
  busy: boolean;
  fetchConversation: (conversationId: string) => Promise<unknown>;
  applyPayload: (payload: unknown) => void;
}): PendingTurnState => {
  const lastMessageId = params.messages[params.messages.length - 1]?.id ?? null;
  // The poll refreshes the verdict without re-applying an unchanged turn.
  const [polled, setPolled] = useState<ServerTurnVerdict>({ messageId: null });
  const running = [polled, params.serverTurn]
    .find((verdict) => verdict?.messageId === lastMessageId && verdict.running !== undefined)?.running;
  const state = params.busy ? 'none' : getPendingTurnState(params.messages, Date.now(), running);
  const latest = useRef(params);
  latest.current = params;

  useEffect(() => {
    if (state !== 'pending' || !params.conversationId) return;
    const conversationId = params.conversationId;
    let canceled = false;
    const timer = window.setInterval(() => {
      latest.current
        .fetchConversation(conversationId)
        .then((payload) => {
          if (canceled) return;
          const next = readMessages(payload);
          const nextLastId = next?.[next.length - 1]?.id ?? null;
          // Only apply when the turn moved, so polling never resets the open pane.
          if (next && nextLastId !== lastMessageId) {
            latest.current.applyPayload(payload);
            return;
          }
          setPolled(readServerTurnVerdict(payload));
        })
        .catch((pollError: unknown) => {
          console.warn('forge_pending_turn_poll_failed', pollError);
        });
    }, POLL_INTERVAL_MS);
    return () => {
      canceled = true;
      window.clearInterval(timer);
    };
  }, [state, params.conversationId, lastMessageId]);

  return state;
};
